import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ACCOUNT_NATURES, type AccountNature } from "@delta/shared";
import type {
  AccountDto, AccountInput, AccountNumberingDto, AccountNumberingInput, AccountOptionsDto, AccountTypeDto, AccountTypeInput, ChartChildrenDto, ChartNode,
  ChartSearchDto, ChartTreeDto, GroupRef, Level1Dto, NextCodeDto
} from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api, ApiFailure } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Blocked, Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "../settings/common";
import { DrCr, drcrText } from "../ledger/common";
import { Card, groupPairs, usageText, useAccountOptions } from "./common";
import { indentPx } from "../../lib/indent";

// Settings › Indexes Types (groups of accounts), Settings › Accounts Index, the chart of accounts tree and the account
// view, ported from the prototype (erp-settings.js "/settings/accountTypes", "/settings/accountIndex", "/chart",
// "/account/:id"). Level 1 is fixed (five classes). Groups belong to the organization and nest to any depth (rule
// H-1, migration 035); accounts sit in any group and are the only rows that take journal lines. Codes are suggested from the
// codes already beneath the chosen group (rules H-2 to H-4); the suggestion is a preview and the code is given on save.

/** From this level the tree stops indenting further and shows the level number instead (any depth, migration 035). */
const DEEP = 6;

/** "Assets › Current assets › Cash" from a path, class first. */
const pathText = (L: (o: { nameEn: string; nameAr: string }) => string, l1: { nameEn: string; nameAr: string } | null, path: GroupRef[]) =>
  [l1 ? L(l1) : "", ...path.map((g) => `${g.code ? g.code + " " : ""}${L(g)}`)].filter(Boolean).join(" › ");

/** The next code under a parent as the server would give it now, and the reason when it cannot. */
function useNextCode(parent: { parentId?: string; level1Id?: number } | null, on: boolean): NextCodeDto | null {
  const [next, setNext] = useState<NextCodeDto | null>(null);
  const key = parent ? `${parent.parentId ?? ""}|${parent.level1Id ?? ""}` : "";
  useEffect(() => {
    setNext(null);
    if (!on || !parent || (!parent.parentId && !parent.level1Id)) return;
    let live = true;
    const q = parent.parentId ? `parentId=${parent.parentId}` : `level1Id=${parent.level1Id}`;
    api<NextCodeDto>("GET", `/accounting/next-code?${q}`).then((n) => { if (live) setNext(n); }, () => undefined);
    return () => { live = false; };
  }, [key, on]);
  return next;
}

/** Why a code is or is not suggested, in words. */
function nextText(t: (k: string, v?: Record<string, string | number>) => string, n: NextCodeDto): string {
  return t(`next_${n.reason}`, { n: n.length && n.prefix ? n.length - n.prefix.length : "", c: n.alternatives.join(", ") });
}

/**
 * The code field of the group and account dialogs. With a suggestion, "Give the next code when saving" is on until
 * the user types a code; the server then gives the next free code (a preview is not a reservation). Without one, the
 * reason is shown and a code is typed.
 */
function CodeField({ id, label, req, hint, value, auto, next, error, onChange, onAuto }: {
  id: string; label: string; req?: boolean; hint: string; value: string; auto: boolean; next: NextCodeDto | null; error?: string;
  onChange(v: string): void; onAuto(on: boolean): void;
}) {
  const { t } = useLang();
  const can = !!next?.code;
  return (
    <Field label={label} req={req && !auto} error={error} htmlFor={id}>
      <input id={id} className="control num" style={{ textAlign: "start" }} dir="ltr" inputMode="numeric" value={auto ? "" : value} placeholder={auto && next?.code ? next.code : ""}
        onChange={(e) => { onChange(e.target.value); if (e.target.value) onAuto(false); }} autoComplete="off" aria-describedby={`${id}-next`} />
      {can && <label className="check" style={{ marginTop: 6 }}><input type="checkbox" id={`${id}-auto`} checked={auto} onChange={(e) => { onAuto(e.target.checked); if (e.target.checked) onChange(""); }} /> {t("chAutoCode")}</label>}
      <span className="hint" id={`${id}-next`} data-testid={`${id}-next`} data-reason={next?.reason ?? ""}>
        {next ? <>{next.code ? <><b dir="ltr">{t("chSuggested", { c: next.code })}</b> · {nextText(t, next)}{auto && <> {t("chSuggestedNote")}</>}</> : nextText(t, next)}</> : hint}
      </span>
    </Field>
  );
}

/** Asks before a move that carries posted history, then saves again with the confirmation. */
function useMoveConfirm(): { ask: (err: unknown, retry: () => void) => boolean; dialog: ReactNode } {
  const { t } = useLang();
  const [pending, setPending] = useState<{ n: number; retry(): void } | null>(null);
  return {
    ask: (err, retry) => {
      if (!(err instanceof ApiFailure && err.code === "move_confirm_required")) return false;
      setPending({ n: err.references.journalLines ?? 0, retry });
      return true;
    },
    dialog: pending && <Confirm title={t("chMoveTitle")} body={t("chMoveBody", { n: pending.n })} okLabel={t("chMoveOk")} onClose={() => setPending(null)} onOk={() => { const r = pending.retry; setPending(null); r(); }} />
  };
}

// ---------- groups: Indexes Types ----------

function TypeForm({ g, level1, opts, preParent, onClose, onSaved }: {
  g: AccountTypeDto | null; level1: Level1Dto[]; opts: AccountOptionsDto | null; preParent?: { parentId: string; level1Id: number }; onClose(): void; onSaved(x: AccountTypeDto, created: boolean): void;
}) {
  const { t, L } = useLang();
  const [v, setV] = useState<{ nameAr: string; nameEn: string; level1Id: string; parentId: string; code: string; nature: string }>(() => ({
    nameAr: g?.nameAr ?? "", nameEn: g?.nameEn ?? "", level1Id: g ? String(g.level1Id) : preParent ? String(preParent.level1Id) : "",
    parentId: g?.parentId ?? preParent?.parentId ?? "", code: g?.code ?? "", nature: g?.nature ?? ""
  }));
  const [auto, setAuto] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const move = useMoveConfirm();
  const locked = !!(g?.accountsCount || g?.groupsCount); // prototype typeL1Locked, extended to the tree; the server refuses the move too
  const set = (k: keyof typeof v, x: string) => { setV((o) => ({ ...o, [k]: x, ...(k === "level1Id" ? { parentId: "" } : {}) })); setErrs((e) => ({ ...e, [k]: "" })); };
  // A new group, or one moved: suggest a code for its new place. An existing group keeps its code unless changed.
  const placeChanged = !g || v.parentId !== (g.parentId ?? "") || v.level1Id !== String(g.level1Id);
  const next = useNextCode(v.level1Id ? (v.parentId ? { parentId: v.parentId } : { level1Id: Number(v.level1Id) }) : null, placeChanged && !g?.code);
  useEffect(() => { if (next?.code && !v.code) setAuto(true); if (!next?.code) setAuto(false); }, [next]);
  // Groups it can go beneath: same class, not itself or anything beneath it.
  const below = useMemo(() => {
    if (!g || !opts) return new Set<string>();
    const out = new Set([g.id]);
    let grew = true;
    while (grew) { grew = false; for (const x of opts.types) if (x.parentId && out.has(x.parentId) && !out.has(x.id)) { out.add(x.id); grew = true; } }
    return out;
  }, [g, opts]);
  const parents = groupPairs(L, (opts?.types ?? []).filter((x) => !below.has(x.id)), Number(v.level1Id) || -1);
  async function save(confirmMove = false) {
    const e: Record<string, string> = {};
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    if (!v.level1Id) e.level1Id = t("requiredField");
    if (!auto && v.code.trim() && !/^\d{1,20}$/.test(v.code.trim())) e.code = t("codeDigits");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    const body: AccountTypeInput = { nameAr: v.nameAr, nameEn: v.nameEn, level1Id: Number(v.level1Id), parentId: v.parentId || null, nature: (v.nature || null) as AccountNature | null,
      ...(auto ? { autoCode: true } : { code: v.code.trim() || null }), ...(confirmMove ? { confirmMove } : {}) };
    try { onSaved(g ? await api<AccountTypeDto>("PUT", `/account-types/${g.id}`, body) : await api<AccountTypeDto>("POST", "/account-types", body), !g); }
    catch (err) {
      if (move.ask(err, () => void save(true))) return;
      setErrs(fieldErrorsOf(t, err));
      setAlert(err instanceof ApiFailure && err.code === "in_use" ? t("typeL1Locked") : errorText(t, err));
    }
    finally { setBusy(false); }
  }
  return (
    <Modal title={t(g ? "editIndexType" : "newIndexType")} wide onClose={onClose} onSubmit={() => void save()} alert={alert} busy={busy}>
      <Field label={t("nameAr")} req error={errs.nameAr} htmlFor="tf-ar"><input id="tf-ar" className="control" lang="ar" dir="rtl" value={v.nameAr} onChange={(e) => set("nameAr", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("nameEn")} req error={errs.nameEn} htmlFor="tf-en"><input id="tf-en" className="control" lang="en" dir="ltr" value={v.nameEn} onChange={(e) => set("nameEn", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("accountIndex")} req error={errs.level1Id} hint={locked ? t("typeL1Locked") : undefined} htmlFor="tf-l1">
        <select id="tf-l1" className="control" value={v.level1Id} disabled={locked} onChange={(e) => set("level1Id", e.target.value)}>
          <option value="">{t("choose")}</option>{level1.map((x) => <option key={x.id} value={x.id}>{L(x)}</option>)}
        </select>
      </Field>
      <Field label={t("chParentGroup")} error={errs.parentId} hint={t("chParentHint")} htmlFor="tf-parent">
        <select id="tf-parent" className="control" value={v.parentId} disabled={!v.level1Id} onChange={(e) => set("parentId", e.target.value)}>
          <option value="">{t("chParentNone")}</option>{parents.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
      </Field>
      <CodeField id="tf-code" label={t("chGroupCode")} hint={t("chGroupCodeHint")} value={v.code} auto={auto} next={next} error={errs.code}
        onChange={(x) => set("code", x)} onAuto={setAuto} />
      <NatureField id="tf-nature" value={v.nature} onChange={(x) => set("nature", x)} />
      {move.dialog}
    </Modal>
  );
}

export function AccountTypes() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const { opts, reload } = useAccountOptions();
  const [rows, setRows] = useState<AccountTypeDto[] | null>(null);
  const [level1, setLevel1] = useState<Level1Dto[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<AccountTypeDto | "new" | { parentId: string; level1Id: number } | null>(null);
  const [deleting, setDeleting] = useState<AccountTypeDto | null>(null);
  const [blocked, setBlocked] = useState<AccountTypeDto | null>(null);

  const load = useCallback(async () => {
    setFailed(null);
    try { setRows(await api<AccountTypeDto[]>("GET", `/account-types${filters.l1 ? "?level1Id=" + filters.l1 : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [filters.l1, t]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { api<Level1Dto[]>("GET", "/account-levels").then(setLevel1, () => undefined); }, []);

  async function doDelete(g: AccountTypeDto) {
    setDeleting(null);
    try { await api("DELETE", `/account-types/${g.id}`); toast(t("deletedOk", { x: L(g) })); reload(); }
    catch (e) { toast(errorText(t, e)); }
    await load();
  }
  const l1Name = (g: AccountTypeDto) => L({ nameEn: g.level1NameEn, nameAr: g.level1NameAr });
  const indent = (g: AccountTypeDto) => ({ paddingInlineStart: `${indentPx(g.depth - 1, 18)}px` });
  const columns: Column<AccountTypeDto>[] = [
    { k: "ar", label: t("nameAr"), cell: (g) => <span lang="ar" className="cell-name" style={indent(g)}>{g.nameAr}</span>, csv: (g) => g.nameAr },
    { k: "en", label: t("nameEn"), cell: (g) => <span lang="en">{g.nameEn}</span>, csv: (g) => g.nameEn },
    { k: "code", label: t("chGroupCode"), cell: (g) => <span className="num" dir="ltr">{g.code ?? ""}</span>, csv: (g) => g.code ?? "" },
    { k: "l1", label: t("mainIndex"), cell: l1Name, csv: l1Name },
    { k: "path", label: t("chParentGroup"), cell: (g) => <span className="muted">{g.path.map((x) => L(x)).join(" › ")}</span>, csv: (g) => g.path.map((x) => L(x)).join(" › ") },
    { k: "lv", label: t("chDepth"), end: true, cell: (g) => <span className="num">{g.depth}</span>, csv: (g) => g.depth },
    { k: "g", label: t("chGroupsCount"), end: true, cell: (g) => <span className="num">{g.groupsCount}</span>, csv: (g) => g.groupsCount },
    { k: "n", label: t("accountsCount"), end: true, cell: (g) => <Link to={`/settings/accountIndex?type=${g.id}`} className="num">{g.accountsCount}</Link>, csv: (g) => g.accountsCount }
  ];
  const newBtn = s.can("chartOfAccountLevel2Add") ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t("newIndex")}</button>
    : <LockedButton label={t("newIndex")} title={t("noPermissionFor", { p: "chartOfAccountLevel2Add" })} />;

  return (
    <Shell navKey="/settings/accountTypes">
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("indexesTypes")]]} title={t("indexesTypes")} />
      <ListView rows={rows} error={failed} columns={columns} file="account-types" testId="account-types-table" onRefresh={() => void load()}
        search={[{ k: "l1", label: t("mainIndex"), type: "select", options: level1.map((x) => [String(x.id), L(x)]) }]} filters={filters} onFilters={setFilters}
        above={<div className="hint pad"><Icon name="info" /> {t("level1Fixed")} {t("chParentHint")}</div>}
        quick={(g) => `${g.code ?? ""} ${g.nameEn} ${g.nameAr}`} newButton={newBtn} tools={<NumberingButton />}
        empty={{ icon: "ledger", title: t("noResults"), body: t("noResultsBody") }}
        actions={(g) => [
          { label: t("view"), icon: "eye", run: () => nav(`/settings/accountIndex?type=${g.id}`), ...(s.can("chartOfAccountLevel3s") ? {} : lockTag(t)) },
          { label: t("edit"), icon: "edit", run: () => setEditing(g), ...(s.can("chartOfAccountLevel2Update") ? {} : lockTag(t)) },
          { label: t("chNewSubgroup"), icon: "plus", run: () => setEditing({ parentId: g.id, level1Id: g.level1Id }), ...(s.can("chartOfAccountLevel2Add") ? {} : lockTag(t)) },
          { label: "", sep: true },
          { label: t("delete"), icon: "trash", danger: true, run: () => (g.accountsCount || g.groupsCount ? setBlocked(g) : setDeleting(g)), ...(s.can("chartOfAccountLevel2Delete") ? {} : lockTag(t)) }
        ]} />
      {editing && <TypeForm g={editing === "new" || !("id" in editing) ? null : editing} level1={level1} opts={opts} preParent={editing !== "new" && !("id" in editing) ? editing : undefined}
        onClose={() => setEditing(null)} onSaved={(g, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(g) })); reload(); void load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
      {blocked && <Blocked title={t("cantDelete")} body={`${t("typeHasAccounts")} (${[blocked.accountsCount ? t("refCount_accounts", { n: blocked.accountsCount }) : "", blocked.groupsCount ? t("refCount_groups", { n: blocked.groupsCount }) : ""].filter(Boolean).join(t("listSep"))})`} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}

// ---------- account numbering settings (rules H-2 to H-4) ----------

function NumberingButton() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return <>
    <button className="btn btn-sm" onClick={() => setOpen(true)} data-testid="numbering-btn"><Icon name="settings" /><span className="hide-sm">{t("numTitle")}</span></button>
    {open && <NumberingDialog onClose={() => setOpen(false)} />}
  </>;
}

function NumberingDialog({ onClose }: { onClose(): void }) {
  const { t } = useLang();
  const s = useSession();
  const toast = useToast();
  const [d, setD] = useState<AccountNumberingDto | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api<AccountNumberingDto>("GET", "/accounting/numbering").then(setD, (e) => setAlert(errorText(t, e))); }, [t]);
  const edit = s.owner;
  const set = <K extends keyof AccountNumberingInput>(k: K, x: AccountNumberingInput[K]) => setD((o) => (o ? { ...o, [k]: x } : o));
  async function save() {
    if (!d || !edit) { onClose(); return; }
    setBusy(true); setAlert(null);
    const body: AccountNumberingInput = { autoSuggest: d.autoSuggest, allowManual: d.allowManual, childSegmentLength: d.childSegmentLength, maxLength: d.maxLength, reuseReleased: d.reuseReleased, requireParentPrefix: d.requireParentPrefix };
    try { await api("PUT", "/accounting/numbering", body); toast(t("numSaved")); onClose(); }
    catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  const check = (k: "autoSuggest" | "allowManual" | "reuseReleased" | "requireParentPrefix", label: string) =>
    <label className="check full" style={{ gridColumn: "1 / -1" }}><input type="checkbox" id={`num-${k}`} checked={!!d?.[k]} disabled={!edit} onChange={(e) => set(k, e.target.checked)} /> {label}</label>;
  return (
    <Modal title={t("numTitle")} wide onClose={onClose} onSubmit={() => void save()} alert={alert} busy={busy}>
      <p className="hint" style={{ gridColumn: "1 / -1" }}>{t("numIntro")} {!edit && <b data-testid="numbering-owner-only">{t("numOwnerOnly")}</b>}</p>
      {d && <>
        {check("autoSuggest", t("numAutoSuggest"))}
        {check("allowManual", t("numAllowManual"))}
        <Field label={t("numSegment")} error={errs.childSegmentLength} htmlFor="num-seg">
          <select id="num-seg" className="control" value={d.childSegmentLength ?? ""} disabled={!edit} onChange={(e) => set("childSegmentLength", e.target.value ? Number(e.target.value) : null)}>
            <option value="">{t("numSegmentNone")}</option>{[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <Field label={t("numMaxLength")} error={errs.maxLength} htmlFor="num-max">
          <input id="num-max" className="control num" type="number" min={1} max={20} value={d.maxLength} disabled={!edit} onChange={(e) => set("maxLength", Number(e.target.value))} />
        </Field>
        {check("reuseReleased", t("numReuse"))}
        {check("requireParentPrefix", t("numPrefix"))}
        <section className="full" style={{ gridColumn: "1 / -1" }} data-testid="numbering-observed">
          <h3 style={{ fontSize: 14, margin: "8px 0" }}>{t("numObserved")}</h3>
          <p className="hint">{t("numLengths")}: <span dir="ltr" className="num">{d.observed.accountCodeLengths.map((x) => `${x.length}: ${x.count}`).join(" · ") || "–"}</span></p>
          <p className="hint">{t("numGroupsCode", { a: d.observed.groupsWithCode, b: d.observed.groupsWithoutCode })}</p>
          <p className="hint">{t("numReleased", { n: d.observed.released })} · {t("numZeros", { n: d.observed.leadingZeros })}</p>
        </section>
      </>}
    </Modal>
  );
}

// ---------- accounts: Accounts Index ----------

/**
 * The side the account normally carries (migration 037). Information only: shown with the account and in printed
 * charts; an entry on the other side still posts (contra and settlement accounts, reversals).
 */
function NatureField({ id, value, onChange }: { id: string; value: string; onChange(x: string): void }) {
  const { t } = useLang();
  return (
    <Field label={t("tplNature")} hint={t("natureHint")} htmlFor={id}>
      <select id={id} className="control" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t("natureNone")}</option>{ACCOUNT_NATURES.map((n) => <option key={n} value={n}>{t(`nature_${n}`)}</option>)}
      </select>
    </Field>
  );
}

export function AccountForm({ a, opts, preType, onClose, onSaved }: { a: AccountDto | null; opts: AccountOptionsDto | null; preType?: string; onClose(): void; onSaved(x: AccountDto, created: boolean): void }) {
  const { t, L } = useLang();
  const [v, setV] = useState<AccountInput>(() => a ? { nameAr: a.nameAr, nameEn: a.nameEn, code: a.code, typeId: a.typeId, nature: a.nature } : { nameAr: "", nameEn: "", code: "", typeId: preType ?? "", nature: null });
  const [auto, setAuto] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const move = useMoveConfirm();
  const set = (k: keyof AccountInput, x: string) => { setV((o) => ({ ...o, [k]: k === "nature" ? x || null : x })); setErrs((e) => ({ ...e, [k]: "" })); };
  // A new account gets a suggestion for its group; an existing one keeps its code (nothing is renumbered).
  const next = useNextCode(v.typeId ? { parentId: v.typeId } : null, !a);
  useEffect(() => { if (next?.code && !v.code) setAuto(true); if (!next?.code) setAuto(false); }, [next]);
  async function save(confirmMove = false) {
    const e: Record<string, string> = {};
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    const code = v.code.trim();
    if (!auto) {
      if (!code) e.code = t("requiredField");
      else if (!/^\d{1,20}$/.test(code)) e.code = t("codeDigits");
    }
    if (!v.typeId) e.typeId = t("requiredField");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    const body: AccountInput = { ...v, code: auto ? "" : code, ...(auto ? { autoCode: true } : {}), ...(confirmMove ? { confirmMove } : {}) };
    try { onSaved(a ? await api<AccountDto>("PUT", `/accounts/${a.id}`, body) : await api<AccountDto>("POST", "/accounts", body), !a); }
    catch (err) { if (move.ask(err, () => void save(true))) return; setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  const classes = opts?.level1 ?? [];
  return (
    <Modal title={t(a ? "editAccountIndex" : "newAccountIndex")} wide onClose={onClose} onSubmit={() => void save()} alert={alert} busy={busy}>
      <Field label={t("nameAr")} req error={errs.nameAr} htmlFor="af-ar"><input id="af-ar" className="control" lang="ar" dir="rtl" value={v.nameAr} onChange={(e) => set("nameAr", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("nameEn")} req error={errs.nameEn} htmlFor="af-en"><input id="af-en" className="control" lang="en" dir="ltr" value={v.nameEn} onChange={(e) => set("nameEn", e.target.value)} autoComplete="off" /></Field>
      <CodeField id="af-code" label={t("indexCode")} req hint={t("accCodeHint")} value={v.code} auto={auto} next={next} error={errs.code} onChange={(x) => set("code", x)} onAuto={setAuto} />
      <Field label={t("mainIndex")} req error={errs.typeId} htmlFor="af-type">
        <select id="af-type" className="control" value={v.typeId} onChange={(e) => set("typeId", e.target.value)}>
          <option value="">{t("choose")}</option>
          {classes.map((l1) => <optgroup key={l1.id} label={L(l1)}>{groupPairs(L, opts!.types, l1.id).map(([id, label]) => <option key={id} value={id}>{label}</option>)}</optgroup>)}
        </select>
      </Field>
      <NatureField id="af-nature" value={v.nature ?? ""} onChange={(x) => set("nature", x)} />
      {move.dialog}
    </Modal>
  );
}

export function Accounts() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { opts, reload } = useAccountOptions();
  const [rows, setRows] = useState<AccountDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<Record<string, string>>(() => ({ type: params.get("type") ?? "" }));
  const [editing, setEditing] = useState<AccountDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<AccountDto | null>(null);
  const [blocked, setBlocked] = useState<AccountDto | null>(null);
  // The group filter follows the address (?type=, opened from Indexes Types), so it survives a reload.
  const setFilters = (f: Record<string, string>) => { setFiltersState(f); setParams(f.type ? { type: f.type } : {}, { replace: true }); };
  useEffect(() => { setFiltersState((f) => (f.type === (params.get("type") ?? "") ? f : { ...f, type: params.get("type") ?? "" })); }, [params]);

  const load = useCallback(async () => {
    setFailed(null);
    const p = new URLSearchParams();
    if (filters.name) p.set("q", filters.name);
    if (filters.type) p.set("typeId", filters.type);
    try { setRows(await api<AccountDto[]>("GET", `/accounts${p.size ? "?" + p : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, filters.type, t]);
  useEffect(() => { void load(); }, [load]);

  async function doDelete(a: AccountDto) {
    setDeleting(null);
    try { await api("DELETE", `/accounts/${a.id}`); toast(t("deletedOk", { x: `${a.code} ${L(a)}` })); reload(); }
    catch (e) { toast(errorText(t, e)); }
    await load();
  }
  const typeName = (a: AccountDto) => L({ nameEn: a.typeNameEn, nameAr: a.typeNameAr });
  const where = (a: AccountDto) => a.path.slice(0, -1).map((x) => L(x)).join(" › ");
  const columns: Column<AccountDto>[] = [
    { k: "ar", label: t("nameAr"), cell: (a) => <Link to={`/account/${a.id}`} lang="ar" className="cell-name">{a.nameAr}</Link>, csv: (a) => a.nameAr },
    { k: "en", label: t("nameEn"), cell: (a) => <span lang="en">{a.nameEn}</span>, csv: (a) => a.nameEn },
    { k: "code", label: t("indexCode"), cell: (a) => <span className="num" dir="ltr">{a.code}</span>, csv: (a) => a.code },
    { k: "l2", label: t("mainIndex"), cell: (a) => <>{typeName(a)}{a.path.length > 1 && <span className="muted d-block" style={{ fontSize: 12 }}>{where(a)}</span>}</>, csv: (a) => [where(a), typeName(a)].filter(Boolean).join(" › ") },
    { k: "bal", label: t("balance"), end: true, cell: (a) => <DrCr v={a.balance} />, csv: (a) => drcrText(t, a.balance) }
  ];
  const newBtn = s.can("chartOfAccountLevel3Add") ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t("newIndex")}</button>
    : <LockedButton label={t("newIndex")} title={t("noPermissionFor", { p: "chartOfAccountLevel3Add" })} />;
  const treeBtn = <>{s.can("r1chartOfAccountsTree") ? <Link className="btn btn-sm" to="/chart"><Icon name="ledger" /><span className="hide-sm">{t("chartTree")}</span></Link> : null}<NumberingButton /></>;
  const typeOpts = useMemo<[string, string][]>(() => groupPairs(L, opts?.types ?? []), [opts, L]);
  const g = opts?.types.find((x) => x.id === filters.type);

  return (
    <Shell navKey="/settings/accountIndex">
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("accountsIndex"), g ? "/settings/accountIndex" : undefined], ...(g ? [[L(g)] as [string]] : [])]} title={t("accountsIndex")} />
      <ListView rows={rows} error={failed} columns={columns} file="chart-of-accounts" testId="accounts-table" onRefresh={() => void load()}
        search={[{ k: "name", label: t("name"), type: "text", hint: t("searchAccount") }, { k: "type", label: t("mainIndex"), type: "select", options: typeOpts }]} filters={filters} onFilters={setFilters}
        quick={(a) => `${a.code} ${a.nameEn} ${a.nameAr}`} tools={treeBtn} newButton={newBtn}
        empty={{ icon: "ledger", title: t("noResults"), body: t("noResultsBody") }}
        actions={(a) => [
          { label: t("view"), icon: "eye", run: () => nav(`/account/${a.id}`) },
          { label: t("edit"), icon: "edit", run: () => setEditing(a), ...(s.can("chartOfAccountLevel3Update") ? {} : lockTag(t)) },
          { label: "", sep: true },
          // A linked account is kept: the prototype explains why instead of asking to confirm (the server refuses too).
          { label: t("delete"), icon: "trash", danger: true, run: () => (usageText(t, a.usedBy) ? setBlocked(a) : setDeleting(a)), ...(s.can("chartOfAccountLevel3Delete") ? {} : lockTag(t)) }
        ]} />
      {editing && <AccountForm a={editing === "new" ? null : editing} opts={opts} preType={filters.type} onClose={() => setEditing(null)}
        onSaved={(a, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: `${a.code} ${L(a)}` })); reload(); void load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
      {blocked && <Blocked title={t("cantDelete")} body={`${t("accountLinked")} (${usageText(t, blocked.usedBy)})`} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}

// ---------- the chart of accounts tree ----------

/**
 * The tree loads a level at a time (rule H-5): the classes and their top groups first, then each group's children
 * when it is opened. Expand all asks for the whole chart (refused above 5,000 groups and accounts). Search runs on the
 * server over the whole chart and shows each hit with its path.
 */
export function ChartTree() {
  const { t, L } = useLang();
  const [tree, setTree] = useState<ChartTreeDto | null>(null);
  const [kids, setKids] = useState<Record<string, ChartNode[]>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string> | null>(null); // null: every class open, groups closed
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<ChartSearchDto | null>(null);
  const load = useCallback(async () => {
    setFailed(null);
    try { setTree(await api<ChartTreeDto>("GET", "/accounting/tree")); setKids({}); } catch (e) { setFailed(errorText(t, e)); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);
  const term = q.trim();
  useEffect(() => {
    if (!term) { setHits(null); return; }
    let live = true;
    const h = setTimeout(() => { api<ChartSearchDto>("GET", `/accounting/tree-search?q=${encodeURIComponent(term)}`).then((x) => { if (live) setHits(x); }, (e) => setFailed(errorText(t, e))); }, 200);
    return () => { live = false; clearTimeout(h); };
  }, [term, t]);

  const isOpen = (id: string) => (open ? open.has(id) : id.startsWith("L"));
  async function toggle(id: string) {
    const was = isOpen(id);
    setOpen((o) => { const n = new Set(o ?? (tree?.level1 ?? []).map((x) => "L" + x.id)); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    if (!was && !id.startsWith("L") && !kids[id]) {
      try { const c = await api<ChartChildrenDto>("GET", `/accounting/tree/${id}`); setKids((k) => ({ ...k, [id]: c.children })); }
      catch (e) { setFailed(errorText(t, e)); }
    }
  }
  async function expandAll() {
    try {
      const all = await api<ChartTreeDto & { children: Record<string, ChartNode[]> }>("GET", "/accounting/tree?all=true");
      setTree(all); setKids(all.children);
      setOpen(new Set([...all.level1.map((x) => "L" + x.id), ...Object.keys(all.children)]));
    } catch (e) { setFailed(errorText(t, e)); }
  }
  const leaf = (a: ChartNode, extra?: ReactNode) => (
    <li key={a.id} className="tr-leaf" role="treeitem" aria-selected={false}><div className="tr-row"><span className="tr-dot" /><span className="tr-code num" dir="ltr">{a.code}</span><Link to={`/account/${a.id}`} className="tr-label">{L(a)}</Link>{a.nature && <span className="tr-nat">{t(`nature_${a.nature}`)}</span>}{extra}</div></li>
  );
  const node = (id: string, label: string, code: string | null, sub: ReactNode, lvl: number, count?: number, nature?: AccountNature | null) => (
    <li key={id} className={`tr-node lvl${Math.min(lvl, 3)}${lvl >= DEEP ? " deep" : ""}`} role="treeitem" aria-expanded={isOpen(id)} aria-selected={false} data-depth={lvl}>
      <div className="tr-row"><button type="button" className="tr-tog" onClick={() => void toggle(id)} aria-label={label}><Icon name="chev" className={isOpen(id) ? "" : "tr-closed"} /></button>
        {lvl >= DEEP && <span className="tr-lv num" title={t("chDepth")}>{lvl}</span>}{code && <span className="tr-code num" dir="ltr">{code}</span>}<span className="tr-label">{label}</span>{nature && <span className="tr-nat">{t(`nature_${nature}`)}</span>}{count != null && <span className="muted num" style={{ fontSize: 12 }}>{count}</span>}</div>
      {isOpen(id) && <ul role="group">{sub}</ul>}
    </li>
  );
  const group = (g: ChartNode, lvl: number): ReactNode => {
    const c = kids[g.id];
    const body = !isOpen(g.id) ? null : !c ? <li className="tr-empty muted">{t("chLoadMore")}</li>
      : c.length ? c.map((x) => (x.kind === "group" ? group(x, lvl + 1) : leaf(x))) : <li className="tr-empty muted">{t("noAccountsYet")}</li>;
    return node(g.id, L(g), g.code, body, lvl, g.groups + g.accounts, g.nature);
  };
  const items = tree?.level1.map((l1) => node("L" + l1.id, L(l1), null, l1.children.map((g) => group(g, 2)), 1));
  const l1Of = (id: number) => tree?.level1.find((x) => x.id === id) ?? null;
  const results = hits && <>
    <div className="tree-head"><span>{t("chSearchHits", { n: hits.hits.length })}</span></div>
    <ul className="coa-tree" role="tree" data-testid="coa-search">{hits.hits.map((h) => {
      const where = <span className="muted" style={{ fontSize: 12 }} data-testid="coa-hit-path">{pathText(L, l1Of(h.level1Id), h.path)}</span>;
      return h.node.kind === "account" ? leaf(h.node, where)
        : <li key={h.node.id} className="tr-node" role="treeitem" aria-selected={false}><div className="tr-row"><Icon name="ledger" />{h.node.code && <span className="tr-code num" dir="ltr">{h.node.code}</span>}<Link to={`/settings/accountIndex?type=${h.node.id}`} className="tr-label">{L(h.node)}</Link>{where}</div></li>;
    })}</ul>
    {hits.more && <p className="hint pad">{t("chSearchMore")}</p>}
  </>;

  const body = failed ? <div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div>
    : !tree ? <div className="state-box"><p>{t("loading")}</p></div>
    : hits ? (hits.hits.length ? results : <div className="state-box"><Icon name="search" /><h3>{t("noResults")}</h3><p>{t("noResultsBody")}</p></div>)
    : <><div className="tree-head"><span>{t("chartOfAccounts")}</span></div><ul className="coa-tree" role="tree" data-testid="coa-tree">{items}</ul></>;
  return (
    <Shell navKey="/settings/accountIndex">
      <PageHead crumbs={[[t("home"), "/home"], [t("accountsIndex"), "/settings/accountIndex"], [t("chartTree")]]} title={t("chartTree")} />
      <section className="card list-card">
        <div className="ltb">
          <div className="input-group lq"><span className="lead"><Icon name="search" /></span><input className="control" type="search" placeholder={t("searchAccount")} aria-label={t("searchAccount")} value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <span className="spacer" />
          <button className="btn btn-sm" onClick={() => void expandAll()} title={t("chExpandAllNote")}>{t("expandAll")}</button>
          <button className="btn btn-sm" onClick={() => setOpen(new Set())}>{t("collapseAll")}</button>
          <Link className="btn btn-sm" to="/settings/accountIndex"><Icon name="table" />{t("accountsIndex")}</Link>
          <Link className="btn btn-sm" to="/settings/chart-template" data-testid="coa-template-link"><Icon name="download" />{t("setChartTemplate")}</Link>
        </div>
        {body}
        <div className="hint pad">{t("treeBalancesPending")}</div>
      </section>
    </Shell>
  );
}

// ---------- the account view ----------

export function AccountShow() {
  const { id } = useParams();
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const { opts, reload } = useAccountOptions();
  const [a, setA] = useState<AccountDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => {
    setFailed(null);
    api<AccountDto>("GET", `/accounts/${id}`).then(setA, (e) => setFailed(e instanceof ApiFailure && e.status === 404 ? t("accountNotFound") : errorText(t, e)));
  }, [id, t]);
  useEffect(() => { setA(null); load(); }, [load]);

  const crumbs = (x?: string): [string, string?][] => [[t("home"), "/home"], [t("settings")], [t("accountsIndex"), "/settings/accountIndex"], ...(x ? [[x] as [string]] : [])];
  if (!a) return (
    <Shell navKey="/settings/accountIndex"><PageHead crumbs={crumbs()} title={t("accountsIndex")} />
      <section className="card"><div className={`state-box ${failed ? "error" : ""}`}>{failed ? <><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p></> : <p>{t("loading")}</p>}</div></section>
    </Shell>
  );
  const used = usageText(t, a.usedBy);
  const groupLink = (g: GroupRef) => (s.can("chartOfAccountLevel3s") ? <Link key={g.id} to={`/settings/accountIndex?type=${g.id}`}>{L(g)}</Link> : <span key={g.id}>{L(g)}</span>);
  const kv: [string, ReactNode][] = [
    [t("nameAr"), <span lang="ar">{a.nameAr}</span>], [t("nameEn"), <span lang="en">{a.nameEn}</span>],
    [t("indexCode"), <span className="num" dir="ltr">{a.code}</span>],
    [t("mainIndex"), s.can("chartOfAccountLevel2s") ? <Link to={`/settings/accountIndex?type=${a.typeId}`}>{L({ nameEn: a.typeNameEn, nameAr: a.typeNameAr })}</Link> : L({ nameEn: a.typeNameEn, nameAr: a.typeNameAr })],
    [t("accountIndex"), L({ nameEn: a.level1NameEn, nameAr: a.level1NameAr })],
    [t("tplNature"), a.nature ? t(`nature_${a.nature}`) : <span className="muted">{t("natureNone")}</span>],
    [t("chPath"), <span data-testid="account-path">{L({ nameEn: a.level1NameEn, nameAr: a.level1NameAr })}{a.path.map((g) => <span key={g.id}> › {groupLink(g)}</span>)}</span>]
  ];
  const actions = s.can("chartOfAccountLevel3Update") ? <button className="btn" onClick={() => setEditing(true)}><Icon name="edit" />{t("edit")}</button> : null;
  return (
    <Shell navKey="/settings/accountIndex">
      <PageHead crumbs={crumbs(`${a.code} ${L(a)}`)} title={`${a.code} ${L(a)}`} actions={actions} />
      <div className="detail-grid"><div className="inv-stack">
        <Card title={t("accountData")}><dl className="kv" style={{ ["--kv-cols" as string]: 3 }} data-testid="account-data">{kv.map(([k, x]) => <div key={k}><dt>{k}</dt><dd>{x}</dd></div>)}</dl></Card>
        <Card title={t("movements")}>{s.can("r1chartOfAccountLevel3Statment")
          ? <p><Link className="btn btn-sm" to={`/reports/charts-statement?accountId=${a.id}`}><Icon name="ledger" />{t("chartStatement")}</Link></p>
          : <p className="muted">{t("movementsInStatement")}</p>}</Card>
      </div>
      <aside className="detail-side">
        <Card title={t("balance")}><p data-testid="account-balance"><b><DrCr v={a.balance} /></b></p><p className="hint">{t("balanceAllEntries")}</p></Card>
        <Card title={t("linkedInSettings")}>
          {used ? <p data-testid="account-links"><Icon name="info" /> {t("accountLinked")} ({used})</p> : <p className="muted" data-testid="account-links">{t("accountNotLinked")}</p>}
        </Card>
      </aside></div>
      {editing && <AccountForm a={a} opts={opts} onClose={() => setEditing(false)} onSaved={(x) => { setEditing(false); toast(t("updatedOk", { x: `${x.code} ${L(x)}` })); reload(); load(); }} />}
    </Shell>
  );
}
